


/**
 * replicate the glsl smoothstep; takes a value, min, max 
 * returns 0 if  value < min, 1 if  value > max
 * linear interpolates if value sits within bounds.
 *
 * @export
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
export function smoothStep(value: number, min: number, max: number): number {

  return Math.max(0, Math.min(1, (value - min) / (max - min)));

}

/**
 * replicates glsl step, return 0 if value < bound, else returns 1
 *
 * @export
 * @param {number} value
 * @param {number} bound
 * @returns
 */
export function step(value: number, bound: number) {
  return (value < bound) ? 0 : 1;
}



/**
 * helper to get cookie token if it exists
 * usen internally by get tokem
 * @category utils
 * @returns {string}
 */
export function getCookieToken(): string {
  const name = 'token=';

  const cookie = decodeURIComponent(document.cookie);
  const c = cookie.split(';');

  for (let i = 0; i < c.length; i++) {
    let char = c[i];

    while (char.charAt(0) === ' ') {
      char = char.substring(1);
    }

    if (char.indexOf(name) === 0) {
      return char.substring(name.length, char.length);
    }
  }

  return '';
}

/**
 * Get a token stored in the clients browser
 * If one does not exist it is created and returned
 * @category utils
 * @export
 * @param {number} [length=10] the length of token to generate if non-existant.
 * @returns {string} the final token
 */
export function getToken(): string {
  let token = getCookieToken();

  if (token === '') {
    const newToken = Phaser.Math.RND.uuid();


    const date = new Date();
    date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000));

    const expiryDate = 'expires=' + date.toUTCString();

    document.cookie = 'token=' + newToken + ';' + expiryDate + ';path=/';

    token = newToken;
  }

  return token;
}




/**
 * convert a hex string to that of a number with base16 radix.
 * @category utils
 * @export
 * @param {string} hexString hex string to convert.
 * @returns {number} number of converted colour.
 */
export function hexToBase16Number(hexString: string): number {
  return parseInt(hexString.replace('#', ''), 16);
}

/**
 * convert a number to that of a HEX string.
 * @category utils
 * @export
 * @param {number} num the number to convert to hex string.
 * @returns
 */
export function base16NumberToHex(num: number): string {
  return "#" + num.toString(16);
}



/**
 * get the hashed segment of the URL
 * @category utils
 * @export
 * @param {string} [defaultValue='']
 * @returns {string}
 */
export function getURLHashSegment(defaultValue: string = ''): string {
  let ret = defaultValue;

  if (window.location.hash.length > 1) {
    ret = window.location.hash.replace('#', '');
  }

  return ret;
}

/**
 * merge 2 objects recurisvley, not really recomeneded but fine for small data models.
 * @category utils
 * @export
 * @param {*} obj1 object to modify
 * @param {*} obj2 object to copy values from (take priority.)
 * @returns {*} final object, (also modifies obj1 in place)
 */
export function MergeObjectRecursive(obj1: any, obj2: any): any {
  for (const p in obj2) {
    try {
      // Property in destination object set; update its value.
      if (obj2[p].constructor === Object) {
        obj1[p] = MergeObjectRecursive(obj1[p], obj2[p]);
      } else {
        obj1[p] = obj2[p];
      }
    } catch (e) {
      // Property in destination object not set; create it and set its value.
      obj1[p] = obj2[p];
    }
  }
  return obj1;
}


/**
 * get a scale that will best fit a certain width and height into max dimensions while keeping aspect ratio
 * @category utils
 * @export
 * @param {number} currentWidth original width
 * @param {number} currentHeight original height
 * @param {number} maxWidth the max width required
 * @param {number} maxHeight thie max height required
 * @returns {number} the scale that will best 'contain' the dimensions
 */

export function bestFit(currentWidth: number, currentHeight: number, maxWidth: number, maxHeight: number): number {
  const horizontalScale = maxWidth / currentWidth;
  const verticleScale = maxHeight / currentHeight;
  return Math.min(horizontalScale, verticleScale);
}


/**
 * tarck a google event to google analytics if incorporated in project.
 * @category utils
 * @export
 * @param {string} action
 * @param {string} label
 */
export function googleTrackEvent(action: string, label: string): void {
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  if (typeof gtag === 'function') {
    console.log('sending to google analytics', action, label);
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    gtag('event', action, {
      'event_label': label
    });
  } else {
    console.log('not connected to tracking,', action, label);
  }

}





/**
 * Fisher Yates array shuffle implimentation
 *
 * @export
 * @param {any[]} array to shuffle
 * @returns array shuffled
 */
export function shuffleArrayFY(a: any[]): any[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * generates an array of booleans with a fixed probability of true / false booleans
 *
 * @export
 * @param {number} length of array
 * @param {number} probability that the value is true (0-1)
 * @returns {boolean[]} shuffled array of these booleans.
 */
export function generateFixedProbabilityArray(length: number, probability: number): boolean[] {

  // declare arrays.
  const arrayTrue: Array<boolean> = [];
  const arrayFalse: Array<boolean> = [];

  // set lengths
  arrayTrue.length = Math.round(length * probability);
  arrayFalse.length = length - Math.round(length * probability);


  // fill and shuffle
  return shuffleArrayFY(arrayTrue.fill(true).concat(arrayFalse.fill(false)));
}


/**
 * iterpolates two colours niceley, (each channel is interpolated as to generate a smooth gradient)
 *
 * @export
 * @param {number} startColour start colour
 * @param {number} endColour end couler
 * @param {number} value the distance from the start to the end (usually 0 - 1)
 * @param {number} [max=1] if not scaling from 0 - 1 you can define a new maximum here
 * @returns {number}
 */
export function interpolateColoursNicely(startColour: number, endColour: number, value: number, max: number = 1): number {
  const colour = Phaser.Display.Color.Interpolate.ColorWithColor(
    Phaser.Display.Color.ValueToColor(startColour),
    Phaser.Display.Color.ValueToColor(endColour),
    max,
    value
  );

  return Phaser.Display.Color.GetColor32(colour.r, colour.g, colour.b, colour.a);
}


/**
 * distribute an array of game objects evenly along the horizontal axis
 *
 * @export
 * @param {DistributableGameObject[]} gameObjects array of game objects.
 * @param {number} min the top most y co-ordanate for the bounds.
 * @param {number} max the bottom most y co-ordanate for the bounds.
 * @param {boolean} [isFlush=false] if true the first and last items should touch the bounds defined, if not an even gap is applied to top and bottom
 * @returns {number} the calculated gap size if required for other UI.
 */
export function evenlyDistributeVertically(gameObjects: DistributableGameObject[], min: number, max: number, isFlush: boolean = false): number {
  // work out and global values we may need

  // how many gaps or gutters we require, if flush, this is one less that the objects we have, if not its one more.
  const gapCount = (isFlush) ? gameObjects.length - 1 : gameObjects.length + 1;

  // the gap or gutter size, is based on the negative space of all the items collective width within the range.
  const gapSize = ((max - min) - (gameObjects[0].height * gameObjects.length)) / gapCount;

  // offset is the starting position for the first item to be space.
  const offset = (isFlush) ? gameObjects[0].height / 2 : gameObjects[0].height / 2 + gapSize;


  for (let i = 0; i < gameObjects.length; i++) {

    // space the item.
    gameObjects[i].y = offset + (i * (gapSize + gameObjects[0].height));
  }

  // return gap size if required for other pruposes.
  return gapSize;

}


/**
 * distribute an array of game objects evenly along the horizontal axis
 *
 * @export
 * @param {DistributableGameObject[]} gameObjects array of game objects.
 * @param {number} min the left most x co-ordanate for the bounds.
 * @param {number} max the right most x co-ordanate for the bounds.
 * @param {boolean} [isFlush=false] if true the first and last items should touch the bounds defined, if not an even gap is applied each side
 * @returns {number} the calculated gap size if required for other UI.
 */
export function evenlyDistributeHorizontally(gameObjects: DistributableGameObject[], min: number, max: number, isFlush: boolean = false): number {
  // work out and global values we may need

  // how many gaps or gutters we require, if flush, this is one less that the objects we have, if not its one more.
  const gapCount = (isFlush) ? gameObjects.length - 1 : gameObjects.length + 1;

  // the gap or gutter size, is based on the negative space of all the items collective width within the range.
  const gapSize = ((max - min) - (gameObjects[0].width * gameObjects.length)) / gapCount;

  // offset is the starting position for the first item to be space.
  const offset = (isFlush) ? gameObjects[0].width / 2 : gameObjects[0].width / 2 + gapSize;

  for (let i = 0; i < gameObjects.length; i++) {

    // space the item.
    gameObjects[i].x = offset + (i * (gapSize + gameObjects[0].width));
  }

  // return gap size if required for other pruposes.
  return gapSize;

}

/**
 * insternally used by the distrobution functions defined above.
 *
 * @interface DistributableGameObject
 */
interface DistributableGameObject {
  x: number;
  y: number;
  width: number;
  height: number;
}



export function percent(value: number): string {
  return Math.round(value * 100) + "%";
}

export function currency(value: number): string {

  const decimal = Math.round(value * 100) / 100;

  const stringValue: string = ("" + decimal.toFixed(2));

  return "£ " + stringValue;
}

export function number(value: number): string {
  return value + "";
}